import { Box, Typography, useTheme, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import DescriptionIcon from "@mui/icons-material/Description";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListPageHeader from "components/list/ListPageHeader";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import SupplierFilterSelect from "components/list/SupplierFilterSelect";
import FixedSizeDialog from "components/fixedSizeDialog";
import PurchaseDraftViewForm from "../forms/purchaseDraftForms/purchaseDraftViewForm";
import { currencyFormatter } from "utilities/currencyFormatter";
import { usePurchaseDraftList, useSupplierList } from "api/hooks/inventory";
import { requester } from "api/pesabackend";
import useListDateFilter from "hooks/useListDateFilter";

export default function PurchaseDrafts({ setIsSuccess }) {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg = theme.palette.background.alt;
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    purchaseDraftList, count, isLoadingPurchaseDraftList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange,
    setSearch, setSupplierFilter, setNewPurchaseDraftData,
  } = usePurchaseDraftList(1, business_branch_id);
  const { supplierList } = useSupplierList(1, business_branch_id);

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [viewData, setViewData] = useState({});
  const [clickedItem, setClickedItem] = useState("");

  const handleClickView = async (id) => {
    const res = await requester.get(`/inventory/purchase_draft/get_form/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setOpenView(true); }
  };

  const handleClickEditOpen = async (id) => {
    const res = await requester.get(`/inventory/purchase_draft/get/${id}`);
    if (res.status === 200) navigate("/editPurchaseDraftForm", { state: res.data.data });
  };

  const handleClickDeleteOpen = async (id) => {
    const res = await requester.get(`/inventory/purchase_draft/get_form/${id}`);
    if (res.status === 200) { setViewData(res.data.data); setClickedItem(id); setOpenDelete(true); }
  };

  const handleClickDelete = async () => {
    const res = await requester.post(`/inventory/purchase_draft/delete/${clickedItem}`);
    if (res.status === 201) { setIsSuccess(true); setNewPurchaseDraftData(true); setOpenDelete(false); }
  };

  const columns = [
    { label: "#",            width: 40  },
    { label: "Date Added"               },
    { label: "Invoice No."              },
    { label: "Invoice Date"             },
    { label: "Grand Total", align: "right" },
    { label: "Discount",    align: "right" },
    { label: "Supplier"                 },
    { label: "Actions",     align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <ListPageHeader
        icon={<DescriptionIcon sx={{ fontSize: "22px" }} />}
        title="Draft Purchase Invoices"
        count={count}
      >
        <Button onClick={() => navigate("/purchaseDraftForm")} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Add Draft
        </Button>
      </ListPageHeader>

      <ListFilterBar>
        <ListDateFilterBar embedded dateFilter={displayDateFilter} customRange={customRange}
          onFilterChange={handleFilterChange} onCustomRangeChange={setCustomRange} onCustomApply={handleCustomApply} />
        <ListSearchField placeholder="Search invoice number…" onSearch={setSearch} />
        <SupplierFilterSelect suppliers={supplierList} onChange={setSupplierFilter} />
      </ListFilterBar>

      <ReportTable columns={columns} rows={purchaseDraftList} loading={isLoadingPurchaseDraftList}
        hasMore={hasMore} onLoadMore={loadMore} loadingMore={isLoadingMore}
        emptyMessage="No draft invoices found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>{moment(row.date_added).format("LLL")}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.invoice_number}</Typography></RCell>
            <RCell>{row.purchase_date}</RCell>
            <RCell align="right"><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{currencyFormatter(parseFloat(row.grand_total))}</Typography></RCell>
            <RCell align="right">{currencyFormatter(parseFloat(row.discount))}</RCell>
            <RCell>{row.related_supplier.name}</RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                {[
                  { label: "View", icon: <VisibilityIcon fontSize="inherit" />, color: accent, border: "rgba(29,158,117,0.3)", hover: "rgba(29,158,117,0.08)", fn: () => handleClickView(row.id) },
                  ...(row.status === 0 ? [
                    { label: "Edit", icon: <EditOutlinedIcon fontSize="inherit" />, color: "#BA7517", border: "rgba(186,117,23,0.3)", hover: "rgba(186,117,23,0.08)", fn: () => handleClickEditOpen(row.id) },
                    { label: "Delete", icon: <DeleteOutlineIcon fontSize="inherit" />, color: "#e24b4a", border: "rgba(226,75,74,0.3)", hover: "rgba(226,75,74,0.08)", fn: () => handleClickDeleteOpen(row.id) },
                  ] : []),
                ].map(({ label, icon, color, border: b, hover, fn }) => (
                  <Box key={label} onClick={fn} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: `1px solid ${b}`, cursor: "pointer", color, fontSize: "12px", fontWeight: 500, transition: "all 0.15s", "&:hover": { backgroundColor: hover } }}>
                    <Box sx={{ fontSize: "14px", display: "flex" }}>{icon}</Box> {label}
                  </Box>
                ))}
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <FixedSizeDialog open={openView} handleClose={() => setOpenView(false)} dialogHeading="View Purchase Draft" negativeResponse="Close" maxWidth="lg" scroll="body"
        content={<PurchaseDraftViewForm viewData={viewData} setIsSuccess={setIsSuccess} setOpenView={setOpenView} setNewPurchaseDraftData={setNewPurchaseDraftData} />}
      />
      <FixedSizeDialog open={openDelete} handleClose={() => setOpenDelete(false)} dialogHeading="Delete this draft?" negativeResponse="Cancel"
        positiveResponse="Yes, delete" handleSubmit={handleClickDelete} maxWidth="sm"
        content={
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px", p: "0.875rem 1rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>This will permanently delete the draft purchase invoice. This action cannot be undone.</Typography>
          </Box>
        }
      />
    </Box>
  );
}